import * as React from 'react';
import { FC, useState, useCallback, useMemo } from 'react';
import { ListGroupItem, ListGroup, Modal, ModalHeader, ModalBody, ModalFooter, Badge } from 'reactstrap';
import { SettingsItemContentProps } from '../SettingsItem';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { IconButton } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ServiceConfigurationModal from './ServiceConfigurationModal';
import { GeneralSettingsData, ServiceSettings } from '../../../models';
import { formatEuros } from '../../../shared/string';
import Typography from '../../../components/Typography';
import AppButton from '../../../components/AppButton';

export interface ServicesListSettingsProps extends SettingsItemContentProps {
    values: GeneralSettingsData;
    onChange: (settings: GeneralSettingsData, save?: boolean) => void;
}

const defaultServiceSettings: ServiceSettings = Object.freeze<ServiceSettings>({
    defaultDurationMinutes: 60,
    defaultPrice: 0,
    defaultPriceByOrigin: {},
    defaultRooms: [],
    category: '',
    description: '',
    name: '',
    stockItems: [],
    sendInvoiceBeforeAppointment: false,
    sendInvoiceDaysBeforeAppointment: 3,
});

const ServicesListSettings: FC<ServicesListSettingsProps> = ({
    id,
    values,
    onChange,
}) => {
    const t = useTranslations();

    const services = values.services || {};

    const [editingService, setEditingService] = useState<string | undefined>(undefined);
    const [deletingService, setDeletingService] = useState<string | undefined>(undefined);
    const [newServiceName, setNewServiceName] = useState<string>('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    const closeDeleteModal = useCallback(() => setDeletingService(undefined), []);
    const onAddNew = useCallback(() => setShowAddModal(true), []);
    const onSelect = useCallback((serviceName: string) => () => setEditingService(serviceName), []);
    const onCloseService = useCallback(() => setEditingService(undefined), []);

    const toggleCategory = useCallback((category: string) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(category)) {
                next.delete(category);
            } else {
                next.add(category);
            }
            return next;
        });
    }, []);

    const normalizedNewServiceName = newServiceName.trim().toLocaleLowerCase();
    const serviceNameAlreadyExists = useMemo(
        () => {
            if (!normalizedNewServiceName) return false;
            const matchesName = Object.values(services).some(
                s => (s?.name ?? '').trim().toLocaleLowerCase() === normalizedNewServiceName
            );
            const matchesKey = Object.keys(services).some(
                k => k.trim().toLocaleLowerCase() === normalizedNewServiceName
            );
            return matchesName || matchesKey;
        },
        [normalizedNewServiceName, services]
    );

    const onSaveService = useCallback(
        (serviceName: string, serviceSettings: ServiceSettings) => {
            onChange(
                {
                    ...values,
                    services: {
                        ...services,
                        [serviceName]: serviceSettings,
                    },
                },
                true
            );
            setEditingService(undefined);
        },
        [services, values, onChange]
    );

    const onDeleteService = useCallback(
        (serviceName: string) => {
            setDeletingService(serviceName);
            setEditingService(undefined);
        },
        []
    );

    const confirmDelete = useCallback(() => {
        if (deletingService) {
            const newServices = { ...services };
            delete newServices[deletingService];
            onChange(
                {
                    ...values,
                    services: newServices,
                },
                true
            );
            setDeletingService(undefined);
        }
    }, [deletingService, services, values, onChange]);

    const handleAddService = useCallback(() => {
        const trimmedServiceName = newServiceName.trim();
        if (trimmedServiceName && !serviceNameAlreadyExists) {
            onChange(
                {
                    ...values,
                    services: {
                        ...services,
                        [trimmedServiceName]: { ...defaultServiceSettings, name: trimmedServiceName },
                    },
                },
                true
            );
            setNewServiceName('');
            setShowAddModal(false);
        }
    }, [newServiceName, serviceNameAlreadyExists, services, values, onChange]);

    const sortedServices = useMemo(
        () => Object.entries(services).sort((a, b) => a[1]?.name?.localeCompare(b[1]?.name ?? '') ?? 0).map(([serviceId]) => serviceId),
        [services]
    );
    const serviceCategories = useMemo(
        () => Array.from(new Set(Object.values(services)
            .map(service => service?.category?.trim())
            .filter((category): category is string => !!category)))
            .sort((a, b) => a.localeCompare(b)),
        [services]
    );
    const allCategories = useMemo(
        () => serviceCategories,
        [serviceCategories]
    );
    const existingServiceNames = useMemo(
        () => Object.values(services)
            .map(service => service?.name?.trim())
            .filter((name): name is string => !!name),
        [services]
    );

    const { categorizedGroups, uncategorizedIds, sortedCategoryNames } = useMemo(() => {
        const groups: Record<string, string[]> = {};
        const uncategorizedIds: string[] = [];

        for (const serviceId of sortedServices) {
            const category = services[serviceId]?.category?.trim();
            if (category) {
                if (!groups[category]) groups[category] = [];
                groups[category].push(serviceId);
            } else {
                uncategorizedIds.push(serviceId);
            }
        }

        const sortedNames = Object.keys(groups).sort((a, b) => a.localeCompare(b));
        return { categorizedGroups: groups, uncategorizedIds, sortedCategoryNames: sortedNames };
    }, [sortedServices, services]);

    const allAvailableRooms = Object.keys(values.rooms ?? {})
        .concat(Object.values(values.branches ?? {}).flatMap(b => Object.keys(b.rooms ?? {})));

    const renderServiceRow = (serviceId: string) => (
        <ListGroupItem key={serviceId} style={{ paddingTop: 'var(--spacer-M)', paddingBottom: 'var(--spacer-M)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XXS)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-S)', flexWrap: 'wrap' }}>
                    <AppButton
                        id={`${id}-open-service-${serviceId}-button`}
                        onClick={onSelect(serviceId)}
                        color="link"
                        style={{ padding: 0, fontWeight: 600, textAlign: 'left' }}
                    >
                        {services[serviceId]?.name || serviceId}
                    </AppButton>
                    <Badge color="success">
                        {formatEuros(services[serviceId]?.defaultPrice ?? 0)}
                    </Badge>
                </div>
                {services[serviceId]?.description && (
                    <Typography block size="small" style={{ color: '#777', lineHeight: 1.45 }}>
                        {services[serviceId].description}
                    </Typography>
                )}
            </div>
        </ListGroupItem>
    );

    return (
        <>
            <ListGroup>
                {uncategorizedIds.map(renderServiceRow)}
                {sortedCategoryNames.map(category => (
                    <React.Fragment key={category}>
                        <ListGroupItem
                            onClick={() => toggleCategory(category)}
                            style={{
                                cursor: 'pointer',
                                backgroundColor: 'var(--bg-light)',
                                paddingTop: 'var(--spacer-S)',
                                paddingBottom: 'var(--spacer-S)',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-S)' }}>
                                {expandedCategories.has(category)
                                    ? <ExpandMoreIcon fontSize="small" />
                                    : <ChevronRightIcon fontSize="small" />}
                                <span style={{ fontWeight: 600 }}>{category}</span>
                                <Badge color="secondary">{categorizedGroups[category].length}</Badge>
                            </div>
                        </ListGroupItem>
                        {expandedCategories.has(category) && categorizedGroups[category].map(renderServiceRow)}
                    </React.Fragment>
                ))}
                <ListGroupItem>
                    <IconButton id={`${id}-add-button`} onClick={onAddNew} size="small">
                        <AddCircleIcon />
                        &nbsp;{t(TK.new)}
                    </IconButton>
                </ListGroupItem>
            </ListGroup>

            <ServiceConfigurationModal
                serviceName={editingService}
                defaultValue={editingService ? services[editingService] : undefined}
                clientOrigins={values.clientOrigins}
                rooms={allAvailableRooms}
                categories={allCategories}
                existingServiceNames={existingServiceNames}
                onSave={onSaveService}
                onClose={onCloseService}
                onDelete={onDeleteService}
            />

            <Modal isOpen={showAddModal} toggle={() => setShowAddModal(false)}>
                <ModalHeader toggle={() => setShowAddModal(false)}>{t(TK.addNewService)}</ModalHeader>
                <ModalBody>
                    <input
                        type="text"
                        className="form-control"
                        placeholder={t(TK.serviceName)}
                        value={newServiceName}
                        onChange={e => setNewServiceName(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && handleAddService()}
                        autoFocus
                    />
                    {serviceNameAlreadyExists && (
                        <Typography block size="small" style={{ color: 'var(--danger-color)', marginTop: 'var(--spacer-XS)' }}>
                            {t(TK.serviceNameAlreadyExists)}
                        </Typography>
                    )}
                </ModalBody>
                <ModalFooter>
                    <AppButton color="success" onClick={handleAddService} disabled={!newServiceName.trim() || serviceNameAlreadyExists}>
                        {t(TK.add)}
                    </AppButton>
                    <AppButton color="secondary" onClick={() => setShowAddModal(false)}>
                        {t(TK.cancel)}
                    </AppButton>
                </ModalFooter>
            </Modal>

            <Modal isOpen={!!deletingService} toggle={closeDeleteModal}>
                <ModalHeader toggle={closeDeleteModal}>{t(TK.areYouSure)}</ModalHeader>
                <ModalBody>{t(TK.youRAboutToDelete)}</ModalBody>
                <ModalFooter>
                    <AppButton id={`${id}-confirm-delete-button`} color="danger" onClick={confirmDelete}>
                        {t(TK.delete)}
                    </AppButton>{' '}
                    <AppButton id={`${id}-cancel-delete-button`} color="secondary" onClick={closeDeleteModal}>
                        {t(TK.cancel)}
                    </AppButton>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default ServicesListSettings;
